import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../services/api";
import { COLUMN_PRESETS } from "./ColumnPresets";

/**
 * R6 A3 — Merged Views + Columns panel.
 *
 * Sections:
 *   - Saved Views (custom, editable: rename / default / delete / save-as)
 *   - Presets (built-in, read-only; apply sets columns)
 *   - Columns (checkbox list; reflects whatever is active)
 *
 * Save flows (all inline in the panel):
 *   - "+ Save current"  -> name input + [Save & Activate] [Save only] [Cancel]
 *   - "Save As…"        -> same as above (copy of the active view/preset)
 *   - "Update"          -> [Update & Activate] [Update only] [Cancel]
 *
 * Nothing becomes active unless the user picks "& Activate",
 * or clicks a view/preset row to apply it.
 */
export default function SavedViewsMenu({
  currentSnapshot,
  open,
  onOpenChange,
  activeViewId,
  isDirty,
  onApply,
  onApplyPreset,
  onApplyDefault,
  onColumnsChange,
  onSaveAs,
  onUpdate,
  onDiscard,
  onDefaultChanged,
  onRenamed,
  onDeleted,
  defaultSuggestion,
  columnsDefs,
  visibleColumns,
}) {
  const [views, setViews] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: "auto", right: "auto" });

  const [confirmMode, setConfirmMode] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [saving, setSaving] = useState(false);

  const wrapperRef = useRef(null);
  const panelRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.getSavedViews?.();
      setViews(
        Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [],
      );
    } catch {
      setViews([]);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const btn = wrapperRef.current?.querySelector("button");
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const PANEL_WIDTH = 400;
    const GAP = 8;
    const spaceRight = window.innerWidth - rect.left;
    if (spaceRight >= PANEL_WIDTH + GAP) {
      setPosition({ top: rect.bottom + 6, left: rect.left, right: "auto" });
    } else {
      const rightOffset = Math.max(GAP, window.innerWidth - rect.right);
      setPosition({ top: rect.bottom + 6, left: "auto", right: rightOffset });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        if (confirmMode) {
          setConfirmMode(null);
        } else {
          onOpenChange?.(false);
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange, confirmMode]);

  useEffect(() => {
    if (!open) {
      setRenamingId(null);
      setRenameValue("");
      setConfirmingDeleteId(null);
      setConfirmMode(null);
      setConfirmName("");
    }
  }, [open]);

  const isPresetActive =
    typeof activeViewId === "string" && activeViewId.startsWith("preset:");
  const isDefaultActive = activeViewId === "default";
  const isCustomActive =
    activeViewId !== null &&
    activeViewId !== "default" &&
    !isPresetActive;
  const activeCustomView = isCustomActive
    ? views.find((v) => v.id === activeViewId)
    : null;

  const openSaveAs = () => {
    setConfirmMode("saveAs");
    setConfirmName(defaultSuggestion || "");
  };
  const openSaveCurrent = () => {
    setConfirmMode("saveCurrent");
    setConfirmName(defaultSuggestion || "");
  };
  const openUpdate = () => {
    setConfirmMode("update");
    setConfirmName(activeCustomView?.name || "");
  };
  const cancelConfirm = () => {
    setConfirmMode(null);
    setConfirmName("");
  };

  const commitSave = async (activate) => {
    if (confirmMode === "update") {
      setSaving(true);
      try {
        await onUpdate?.({ activate });
      } finally {
        setSaving(false);
        setConfirmMode(null);
      }
      return;
    }
    const n = confirmName.trim();
    if (!n) return;
    setSaving(true);
    try {
      await onSaveAs?.(n, { activate });
    } finally {
      setSaving(false);
      setConfirmMode(null);
      setConfirmName("");
      await load();
    }
  };

  const commitRename = async (id) => {
    const next = renameValue.trim();
    if (!next) {
      setRenamingId(null);
      return;
    }
    try {
      await api.updateSavedView?.(id, { name: next });
      onRenamed?.({ id, name: next });
    } catch {}
    setRenamingId(null);
    setRenameValue("");
    await load();
  };

  const del = async (id) => {
    try {
      await api.deleteSavedView?.(id);
      onDeleted?.(id);
    } catch {}
    setConfirmingDeleteId(null);
    await load();
  };

  const setDefault = async (id) => {
    try {
      await api.updateSavedView?.(id, { isDefault: true });
      const v = views.find((x) => x.id === id);
      onDefaultChanged?.({ id, name: v?.name, isDefault: true });
    } catch {}
    await load();
  };

  const applyPreset = (key) => {
    const p = COLUMN_PRESETS[key];
    if (!p) return;
    const cols = {};
    for (const def of columnsDefs) cols[def.key] = p.columns.includes(def.key);
    onApplyPreset?.(key, cols);
  };

  const toggleColumn = (key) => {
    const next = { ...visibleColumns, [key]: !visibleColumns[key] };
    onColumnsChange?.(next);
  };

  const showDirtyBar = isDirty && confirmMode === null;

  return (
    <div className="r6-saved-views-menu" ref={wrapperRef}>
      <button
        type="button"
        className={`btn btn-secondary btn-sm ${open ? "is-open" : ""} ${
          activeViewId ? "has-active-view" : ""
        }`}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onOpenChange?.(!open);
        }}
        title="Views and columns"
      >
        <i className="fas fa-bookmark" /> Views
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="r6-menu-backdrop"
              onMouseDown={() => onOpenChange?.(false)}
            />
            <div
              ref={panelRef}
              className="r6-views-panel"
              role="dialog"
              aria-label="Views and columns"
              style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                right: position.right,
              }}
            >
              {confirmMode && (
                <div className="r6-saved-view-confirm">
                  {confirmMode === "update" ? (
                    <div className="r6-saved-view-confirm-msg">
                      Update "{activeCustomView?.name || "view"}"?
                    </div>
                  ) : (
                    <>
                      <div className="r6-saved-view-confirm-msg">
                        {confirmMode === "saveAs"
                          ? "Save as a new view"
                          : "Save current state as a view"}
                      </div>
                      <input
                        className="form-control form-control-sm"
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && confirmName.trim())
                            commitSave(true);
                        }}
                        placeholder={defaultSuggestion || "View name"}
                        autoFocus
                      />
                    </>
                  )}

                  <div className="r6-saved-view-confirm-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => commitSave(true)}
                      disabled={saving || (confirmMode !== "update" && !confirmName.trim())}
                    >
                      {saving
                        ? "Saving…"
                        : confirmMode === "update"
                          ? "Update & Activate"
                          : "Save & Activate"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => commitSave(false)}
                      disabled={saving || (confirmMode !== "update" && !confirmName.trim())}
                    >
                      {confirmMode === "update" ? "Update only" : "Save only"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={cancelConfirm}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ---------- SAVED VIEWS ---------- */}
              <div className="r6-views-section">
                <div className="r6-views-section-head">
                  <span className="r6-views-section-title">Saved Views</span>
                  {!confirmMode && (
                    <button
                      type="button"
                      className="r6-link"
                      onClick={openSaveCurrent}
                    >
                      + Save current
                    </button>
                  )}
                </div>

                {/* Built-in Default row */}
                <div
                  className={`r6-saved-view-row r6-default-row ${
                    isDefaultActive ? "is-active" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="r6-saved-view-apply"
                    onClick={() => {
                      onApplyDefault?.();
                      onOpenChange?.(false);
                    }}
                    title="Apply default layout"
                  >
                    {isDefaultActive && (
                      <i className="fas fa-check" style={{ marginRight: 6 }} />
                    )}
                    Default
                  </button>
                </div>

                {views.length === 0 && (
                  <div className="r6-views-empty">No saved views yet.</div>
                )}

                {views.map((v) => {
                  const isRenaming = renamingId === v.id;
                  const isDeleting = confirmingDeleteId === v.id;
                  const isActive = activeViewId === v.id;
                  return (
                    <div
                      key={v.id}
                      className={`r6-saved-view-row ${isActive ? "is-active" : ""}`}
                    >
                      {isRenaming ? (
                        <>
                          <input
                            className="form-control form-control-sm r6-inline-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename(v.id);
                              if (e.key === "Escape") setRenamingId(null);
                            }}
                            autoFocus
                          />
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => commitRename(v.id)}>Save</button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRenamingId(null)}>Cancel</button>
                        </>
                      ) : isDeleting ? (
                        <>
                          <span className="r6-delete-confirm-text">Delete "{v.name}"?</span>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => del(v.id)}>Yes</button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmingDeleteId(null)}>No</button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="r6-saved-view-apply"
                            onClick={() => {
                              onApply?.(v.payload, v);
                              onOpenChange?.(false);
                            }}
                            title="Apply this view"
                          >
                            {isActive && <i className="fas fa-check" style={{ marginRight: 6 }} />}
                            {v.name}
                            {v.is_default ? " ★" : ""}
                          </button>
                          <div className="r6-saved-view-actions">
                            <button type="button" className="r6-link" onClick={() => { setRenamingId(v.id); setRenameValue(v.name); }}>Rename</button>
                            <button type="button" className="r6-link" onClick={() => setDefault(v.id)} disabled={!!v.is_default} title={v.is_default ? "Already default" : "Set as default"}>
                              {v.is_default ? "Default ★" : "Default"}
                            </button>
                            <button type="button" className="r6-link danger" onClick={() => setConfirmingDeleteId(v.id)}>Delete</button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ---------- PRESETS ---------- */}
              <div className="r6-views-section">
                <div className="r6-views-section-head">
                  <span className="r6-views-section-title">Presets</span>
                  <span className="r6-views-section-hint">read-only</span>
                </div>
                {Object.entries(COLUMN_PRESETS).map(([key, p]) => {
                  const presetId = `preset:${key}`;
                  const isActive = activeViewId === presetId;
                  return (
                    <div
                      key={key}
                      className={`r6-saved-view-row r6-preset-row ${isActive ? "is-active" : ""}`}
                    >
                      <button
                        type="button"
                        className="r6-saved-view-apply"
                        onClick={() => applyPreset(key)}
                        title={`Apply ${p.label}`}
                      >
                        {isActive && <i className="fas fa-check" style={{ marginRight: 6 }} />}
                        {p.label}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* ---------- COLUMNS ---------- */}
              <div className="r6-views-section">
                <div className="r6-views-section-head">
                  <span className="r6-views-section-title">Columns</span>
                  {(isPresetActive || isCustomActive) && (
                    <span className="r6-views-section-hint">
                      toggle to customize
                    </span>
                  )}
                </div>
                <div className="r6-views-columns">
                  {columnsDefs.map((c) => (
                    <label key={c.key} className="r6-views-column-item">
                      <input
                        type="checkbox"
                        checked={!!visibleColumns[c.key]}
                        onChange={() => toggleColumn(c.key)}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* ---------- DIRTY ACTION BAR ---------- */}
              {showDirtyBar && isPresetActive && (
                <div className="r6-saved-views-actionbar">
                  <span className="r6-saved-views-actionbar-label">Preset modified</span>
                  <button type="button" className="btn btn-primary btn-sm" onClick={openSaveAs}>
                    Save As…
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onDiscard?.()}>
                    Discard
                  </button>
                </div>
              )}
              {showDirtyBar && isCustomActive && (
                <div className="r6-saved-views-actionbar">
                  <span className="r6-saved-views-actionbar-label">
                    {activeCustomView?.name || "View"} modified
                  </span>
                  <button type="button" className="btn btn-primary btn-sm" onClick={openUpdate}>
                    Update
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={openSaveAs}>
                    Save As…
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onDiscard?.()}>
                    Discard
                  </button>
                </div>
              )}
              {showDirtyBar && isDefaultActive && (
                <div className="r6-saved-views-actionbar">
                  <span className="r6-saved-views-actionbar-label">Unsaved changes</span>
                  <button type="button" className="btn btn-primary btn-sm" onClick={openSaveCurrent}>
                    Save current
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onDiscard?.()}>
                    Discard
                  </button>
                </div>
              )}
              {showDirtyBar && !isPresetActive && !isCustomActive && !isDefaultActive && (
                <div className="r6-saved-views-actionbar">
                  <span className="r6-saved-views-actionbar-label">Unsaved changes</span>
                  <button type="button" className="btn btn-primary btn-sm" onClick={openSaveCurrent}>
                    Save current
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onDiscard?.()}>
                    Discard
                  </button>
                </div>
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}